import { useUserProfile } from '@/context/UserProfileContext';
import { GamePlayer, GameSetup } from '@/types/golf';
import { api } from '@/utils/api';
import { mediumImpact } from '@/utils/capacitorUtils';
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  MouseSensor,
  TouchSensor,
  UniqueIdentifier,
  closestCenter,
  defaultDropAnimationSideEffects,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy } from '@dnd-kit/sortable';
import {
  IonButton,
  IonButtons,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonPage,
  IonSpinner,
  IonTitle,
  IonToolbar
} from '@ionic/react';
import { addOutline, chevronBackOutline } from 'ionicons/icons';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GolfAvatar } from '../ui/GolfAvatar';
import PlayerEditModal from './PlayerEditModal';
import PlayerEditor from './PlayerEditor';
import PlayerItem from '../player/PlayerItem';
import EmptySlot from '../player/EmptySlot';

interface GameSetupPlayersProps {
  initialData: Partial<GameSetup>;
  onNext: (data: Partial<GameSetup>) => void;
  onBack: () => void;
  nav: any; // Changed from HTMLIonNavElement to any to accept router
  isGameSettings?: boolean;
  onUpdateSuccess?: () => Promise<void>;
}

const GameSetupPlayers = ({ initialData, onNext, onBack, nav, isGameSettings = false, onUpdateSuccess }: GameSetupPlayersProps) => {
  const { profile } = useUserProfile();
  const { t } = useTranslation();

  // State for sorting mode
  const [isSortingMode, setIsSortingMode] = useState(false);

  // State for drag and drop
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [activePlayer, setActivePlayer] = useState<GamePlayer | null>(null);
  const [targetSlot, setTargetSlot] = useState<number | null>(null);

  // State for player edit modal
  const [editingPlayer, setEditingPlayer] = useState<GamePlayer | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);

  // Refs for the droppable slots
  const slotRefs = useRef<(HTMLDivElement | null)[]>([null, null, null, null]);

  // Initialize players array with current user if available and no players exist yet
  const [players, setPlayers] = useState<GamePlayer[]>(() => {
    // If we already have players from initialData, use those
    if (initialData.players && initialData.players.length > 0) {
      return initialData.players;
    }

    if (!profile) {
      return [];
    }
    // Otherwise, add the current user if available, but only on first initialization
    return [
      {
        userId: profile.userId,
        name: profile.name || profile.username,
        handicap: profile.handicap?.toString() || '0',
        teeName: initialData.course?.tees?.[0]?.teeName,
        teeBoxId: initialData.course?.tees?.[0]?.teeId,
        imageUrl: profile.imageUrl,
        email: profile.email
      }
    ];

  });

  // Navigation functions for player management
  const navigateToAddPlayer = () => {
    if (nav) {
      const PlayerEditorWrapper = () => {
        const handleAddPlayer = (player: GamePlayer) => {
          // Ensure player has a default tee assigned if none was selected
          const defaultTee = initialData.course?.tees?.[0];
          const updatedPlayer: GamePlayer = {
            ...player,
            teeBoxId: defaultTee?.teeId?.toString() || '',
            teeName: defaultTee?.teeName || ''
          };

          const newPlayers = [...players, updatedPlayer];
          setPlayers(newPlayers);

          // Update game data in the API
          updateGameData({
            ...initialData,
            players: newPlayers
          });
          
          // Go back to the player list
          nav.pop();
        };

        return (
          <PlayerEditor
            initialPlayer={{
              name: '',
              handicap: 0,
              teeName: initialData.course?.tees?.[0]?.teeName,
              teeBoxId: initialData.course?.tees?.[0]?.teeId?.toString(),
              imageUrl: '',
              email: '',
              userId: ''
            }}
            existingPlayerIds={players.map(p => p.userId)}
            onAddPlayer={handleAddPlayer}
            courseData={initialData.course}
            onCancel={() => nav.pop()}
          />
        );
      };
      
      nav.push(PlayerEditorWrapper);
    } else {
      // Fallback to using a modal or other navigation method
      console.warn('Navigation not available. Using fallback.');
    }
  };

  const openEditPlayerModal = (index: number) => {
    const player = players[index];

    // Convert handicap to number if it's a string
    const playerWithNumberHandicap = {
      ...player,
      handicap: typeof player.handicap === 'string' ? parseInt(String(player.handicap)) : player.handicap
    };

    setEditingPlayer(playerWithNumberHandicap);
    setShowEditModal(true);
  };

  // Handle removing a player
  const handleRemovePlayer = (index: number) => {
    // Don't allow removing the current user
    if (players[index]?.userId === profile?.userId) return;

    const newPlayers = [...players];
    newPlayers.splice(index, 1);
    setPlayers(newPlayers);

    // Update game data in the API
    const updatedPlayers = [...players];
    updatedPlayers.splice(index, 1);
    setPlayers(updatedPlayers);

    // Update game data
    updateGameData({
      ...initialData,
      players: updatedPlayers
    });
  };

  // Handle deleting a player from the modal
  const handleDeletePlayerFromModal = () => {
    if (!editingPlayer) return;
    
    const index = players.findIndex(p => p.userId === editingPlayer.userId);
    if (index !== -1) {
      handleRemovePlayer(index);
    }
    setShowEditModal(false);
    setEditingPlayer(null);
  };
  
  // Handle updating a player from the modal
  const handleUpdatePlayerFromModal = (updatedPlayer: GamePlayer) => {
    const index = players.findIndex(p => p.userId === updatedPlayer.userId);

    // Ensure player has a tee assigned
    const defaultTee = initialData.teeOptions?.default?.slug || 'white';
    const playerWithTee: GamePlayer = {
      ...updatedPlayer,
      teeName: updatedPlayer.teeName || defaultTee
    };

    if (index !== -1) {
      const updatedPlayers = [...players];
      updatedPlayers[index] = playerWithTee;
      setPlayers(updatedPlayers);

      // Update game data in the API
      updateGameData({
        ...initialData,
        players: updatedPlayers
      });
    }
    
    setShowEditModal(false);
    setEditingPlayer(null);
  };

  // State for tracking submission status
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Remove unused imports
  // const history = useHistory();

  // Update game data in the API
  const updateGameData = async (gameData: Partial<GameSetup>) => {
    try {
      if (gameData.gameId) {
        await api.put(`/game/${gameData.gameId}`, gameData);
        
        if (isGameSettings && onUpdateSuccess) {
          await onUpdateSuccess();
        }
      } 
      // Also update initialData to ensure it's preserved when navigating back
      if (initialData.players !== gameData.players) {
        initialData.players = gameData.players || [];
      }
    } catch (error) {
      console.error('Error updating game data:', error);
    }
  };

  const handleNext = async () => {
    try {
      setIsSubmitting(true);
      mediumImpact();

      const submitData = {
        ...initialData,
        players,
        status: 'pending'
      };

      const response = await api.post('/game', submitData);
      const result = response.data;

      // Pass the data back to the parent component with the game ID
      const gameData = {
        ...initialData,
        players,
        gameId: result.gameId
      };

      // Pass the complete data including the gameId to the parent component
      onNext(gameData);
    } catch (error) {
      console.error('Error creating game:', error);
      alert('Failed to create game. Please try again');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Configure sensors for drag and drop
  const sensors = useSensors(
    useSensor(MouseSensor, {
      // Require the mouse to move by 5 pixels before activating
      activationConstraint: {
        distance: 5,
        delay: 250, // 250ms delay
      },
    }),
    useSensor(TouchSensor, {
      // Press delay of 250ms, with tolerance of 5px of movement
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    })
  );

  // Handle drag start
  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    if (!active || !active.id) return;

    setActiveId(active.id);

    // Find the player being dragged
    const draggedPlayer = players.find(p => p.userId === active.id);
    if (draggedPlayer) {
      setActivePlayer(draggedPlayer);
    }

    // Enter sorting mode when dragging starts
    setIsSortingMode(true);
    mediumImpact();
  };

  // Handle drag over
  const handleDragOver = (event: DragOverEvent) => {
    const { over } = event;

    if (over) {
      // Check if we're over a droppable slot
      const isOverEmptySlot = over.data.current?.droppable === true;
      const slotIndex = over.data.current?.slotIndex;

      if (isOverEmptySlot && slotIndex !== undefined) {
        setTargetSlot(slotIndex);
      } else if (over.id !== activeId) {
        // We're over another player
        const overIndex = players.findIndex(p => p.userId === over.id);
        if (overIndex !== -1) {
          setTargetSlot(overIndex);
        }
      }
    } else {
      setTargetSlot(null);
    }
  };

  // Handle drag end
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!active || !over) {
      // Reset state if we don't have valid active/over objects
      setActiveId(null);
      setActivePlayer(null);
      setTargetSlot(null);
      setIsSortingMode(false);
      return;
    }

    if (active.id !== over.id) {
      let newPlayers = [...players];
      // Find the indices
      const activeIndex = players.findIndex(p => p.userId === active.id);

      if (activeIndex === -1) {
        // Player not found, reset state and return
        setActiveId(null);
        setActivePlayer(null);
        setTargetSlot(null);
        setIsSortingMode(false);
        return;
      }

      // Check if we're dropping onto a player or an empty slot
      if (over.data && over.data.current && over.data.current.droppable === true) {
        // Dropping into an empty slot
        const slotIndex = over.data.current.slotIndex;
        if (slotIndex !== undefined) {
          // Move the player to the end (empty slot)
          const [movedPlayer] = newPlayers.splice(activeIndex, 1);
          newPlayers.push(movedPlayer);
        }
      } else {
        // Dropping onto another player
        const overIndex = players.findIndex(p => p.userId === over.id);
        if (overIndex !== -1 && activeIndex !== overIndex) {
          // Reorder the players array
          newPlayers = arrayMove(players, activeIndex, overIndex);
        }
      }

      // Update state and API
      setPlayers(newPlayers);
      updateGameData({
        ...initialData,
        players: newPlayers
      });
    }

    // Reset state
    setActiveId(null);
    setActivePlayer(null);
    setTargetSlot(null);
    setIsSortingMode(false);
  };

  return (
    <IonPage>
      <IonHeader className="ion-no-border">
        <IonToolbar color="light">
          <IonButtons slot="start">
            <IonButton onClick={() => onBack()}>
              <IonIcon slot="icon-only" icon={chevronBackOutline} />
            </IonButton>
          </IonButtons>
          <IonTitle>{t('add_players')}</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent color="light" className="ion-padding">
        <div className='space-y-4'>
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold">{t('players')}</h2>
          </div>

          <div>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDragEnd={handleDragEnd}
              autoScroll={true}
            >
              <SortableContext items={players.map(p => p.userId)} strategy={rectSortingStrategy}>
                <div className="w-full bg-card rounded-lg p-4">
                  <div className="grid grid-cols-4 gap-3">
                    {players.map((player, index) => (
                      <PlayerItem
                        key={player.userId}
                        player={player}
                        isCurrentUser={player.userId === profile?.userId}
                        onClick={() => {
                          if (!isSortingMode) {
                            openEditPlayerModal(index);
                          }
                        }}
                        onRemove={() => {
                          if (player.userId !== profile?.userId) {
                            handleRemovePlayer(index);
                          }
                        }}
                      />
                    ))}

                    {/* Empty Slots / Add Player Buttons - Droppable */}
                    {Array.from({ length: 4 - players.length }).map((_, i) => (
                      <EmptySlot
                        key={`slot-${i}`}
                        index={players.length + i}
                        isOver={targetSlot === players.length + i}
                        onClick={navigateToAddPlayer}
                        ref={(el: HTMLDivElement | null) => slotRefs.current[i] = el}
                      />
                    ))}
                  </div>
                </div>
              </SortableContext>

              {/* Drag Overlay - Shows what's being dragged */}
              <DragOverlay
                adjustScale={true}
                dropAnimation={{
                  sideEffects: defaultDropAnimationSideEffects({
                    styles: {
                      active: {
                        opacity: '0.75',
                      },
                    },
                  }),
                }}
              >
                {activePlayer && (
                  <div className="flex flex-col items-center opacity-90">
                    <div className="relative mb-2">
                      <GolfAvatar
                        name={activePlayer.name}
                        imageUrl={activePlayer.imageUrl}
                        className='aspect-square size-full rounded-lg'
                      />
                    </div>
                    <div className="text-center">
                      <div className="font-medium text-sm">{activePlayer.name.split(' ')[0]}</div>
                    </div>
                  </div>
                )}
              </DragOverlay>
            </DndContext>
          </div>

          {/* WIP Add Group Button - Disabled for now as per requirements */}
          <button
            disabled
            className="w-full flex items-center gap-2 bg-card rounded-xl p-4 opacity-70 hidden"
          >
            <div className="size-10 rounded-lg bg-gray-100 flex items-center justify-center">
              <IonIcon icon={addOutline} className="size-5 text-gray-400" />
            </div>
            <span className="font-bold">{t('add_group')}</span>
          </button>
        </div>
      </IonContent>

      {!isGameSettings && (
        <IonFooter className="ion-no-border">
          <IonToolbar color="light">
            <IonButton
              expand="block"
              shape='round'
              onClick={handleNext}
              disabled={players.length === 0 || isSubmitting}
              className="font-bold"
            >
              {isSubmitting ? (
                <>
                  <IonSpinner name="dots" className="mr-2" />
                  {t('creating_game')}
                </>
              ) : (
                t('start')
              )}
            </IonButton>
          </IonToolbar>
        </IonFooter>
      )}

      {/* Player Edit Modal */}
      {editingPlayer && (
        <PlayerEditModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditingPlayer(null);
          }}
          player={editingPlayer}
          gameSetup={initialData}
          isCurrentUser={editingPlayer.userId === profile?.userId}
          onDeletePlayer={handleDeletePlayerFromModal}
          onUpdatePlayer={handleUpdatePlayerFromModal}
        />
      )}
    </IonPage >
  );
};


export default GameSetupPlayers;
